import Component from '@glimmer/component';
import type Questionnaire from 'garaje/models/questionnaire';
import type QuestionnaireResponseSummary from 'garaje/models/questionnaire-response-summary';

type Status = 'incomplete' | 'not_applicable' | 'not_passed' | 'passed';

const ICON_MAPPING: Record<Status, string> = {
  incomplete: '/assets/images/gray-dash-small.svg',
  not_applicable: '/assets/images/gray-dash-small.svg',
  not_passed: '/assets/images/cross-small-red.svg',
  passed: '/assets/images/checkmark-small-gray.svg',
};

const DISPLAY_VALUE_MAPPING: Record<Status, string> = {
  incomplete: 'Incomplete',
  not_applicable: 'N/A',
  not_passed: 'Failed',
  passed: 'Passed',
};

interface QuestionnairesStatusColumnSignature {
  Args: {
    questionnaires: Questionnaire[];
    questionnaireResponseSummary: QuestionnaireResponseSummary | null;
  };
  Element: HTMLDivElement;
}

export default class QuestionnairesStatusColumn extends Component<QuestionnairesStatusColumnSignature> {
  get status(): Status {
    const { questionnaires, questionnaireResponseSummary } = this.args;

    // If there's no summary, status depends on whether there are questionnaires or not
    if (!questionnaireResponseSummary) {
      return questionnaires.length > 0 ? 'incomplete' : 'not_applicable';
    }

    // If there's a not-"complete" summary, status is "incomplete"
    if (questionnaireResponseSummary.overallStatus !== 'complete') {
      return 'incomplete';
    }

    // Otherwise, we have a "complete" summary, status depends on whether all questionnaires were passed ("passed") or not ("not_passed")
    return questionnaireResponseSummary.totalPassed === questionnaireResponseSummary.totalCompleted
      ? 'passed'
      : 'not_passed';
  }

  get displayStatus(): string {
    return DISPLAY_VALUE_MAPPING[this.status];
  }

  get icon(): string {
    return ICON_MAPPING[this.status];
  }

  <template>
    <div class="table-cell border-b align-top px-2 pt-6 text-carbon-50" ...attributes>
      {{#if this.displayStatus}}
        <div class="flex items-center">
          <img src={{this.icon}} alt="" class="mr-1" role="presentation" />
          {{this.displayStatus}}
        </div>
      {{/if}}
    </div>
  </template>
}
